// מסך הבית: מתחיל מפעולה. קמפיין חדש, הקמפיינים האחרונים, ורק מה שדורש תשומת לב.
import { Link } from 'react-router-dom';
import { ErrorBox, Loading, useLoad, useMe } from '../components/ui';
import Icon from '../components/Icon';
import CampaignCard, { type CampaignRow } from '../components/CampaignCard';

interface Dash {
  jobs: { id: string; type: string; status: string; error: string | null }[];
  pendingSpecs: { id: string; version: number; status: string; placement_name: string; media_name: string }[];
  needsReview: { id: string; display_name: string; needs_review_reason: string | null }[];
}

const greeting = () => { const h = new Date().getHours(); return h < 5 ? 'לילה טוב' : h < 12 ? 'בוקר טוב' : h < 18 ? 'צהריים טובים' : 'ערב טוב'; };

export default function Dashboard() {
  const me = useMe();
  const camps = useLoad<CampaignRow[]>('/api/campaigns?limit=6');
  const dash = useLoad<Dash>('/api/dashboard');
  const failed = dash.data?.jobs.filter((j) => j.status === 'failed') ?? [];
  const running = dash.data?.jobs.filter((j) => j.status === 'queued' || j.status === 'running') ?? [];
  const attention = (dash.data?.pendingSpecs.length ?? 0) + (dash.data?.needsReview.length ?? 0) + failed.length;
  const first = me.user.name.split(' ')[0];
  return (
    <div className="stack" style={{ gap: 32 }}>
      <section className="hero" aria-labelledby="hero-h">
        <div>
          <h1 id="hero-h">{greeting()}, {first}</h1>
          <p>צרו סט באנרים לכל הגדלים בכמה דקות: בוחרים לקוח, אתרים וגדלים, כותבים טקסט, והמערכת מעצבת ומתאימה.</p>
          <div className="row">
            <Link className="btn primary big" to="/new"><Icon name="plus" size={22} />קמפיין חדש</Link>
            <Link className="btn big" to="/ad" style={{ background: 'rgba(255,255,255,.12)', color: '#fff', borderColor: 'rgba(255,255,255,.4)' }}><Icon name="film" size={22} />ממודעה קיימת לבאנרים</Link>
          </div>
        </div>
        <div className="steps-mini" aria-hidden="true">
          <div><b>1</b>לקוח ומיתוג</div>
          <div><b>2</b>אתרים וגדלים</div>
          <div><b>3</b>טקסטים ותמונה</div>
          <div><b>4</b>אישור, והורדה של הכול</div>
        </div>
      </section>

      <ErrorBox error={camps.error ?? dash.error} />

      <section aria-labelledby="recent-h">
        <div className="row between"><h2 id="recent-h" style={{ margin: 0 }}>הקמפיינים האחרונים</h2><Link to="/campaigns">כל הקמפיינים</Link></div>
        {!camps.data && camps.loading && <Loading />}
        {camps.data && camps.data.length === 0 && (
          <div className="card empty" style={{ marginTop: 16 }}>
            <Icon name="megaphone" size={32} />
            <p style={{ margin: '8px 0 16px' }}>עדיין אין קמפיינים. הראשון לוקח כמה דקות.</p>
            <Link className="btn primary" to="/new"><Icon name="plus" />יצירת קמפיין ראשון</Link>
          </div>
        )}
        {camps.data && camps.data.length > 0 && <div className="cards" style={{ marginTop: 16 }}>{camps.data.map((c) => <CampaignCard key={c.id} c={c} />)}</div>}
      </section>

      {(attention > 0 || running.length > 0) && (
        <section aria-labelledby="att-h">
          <h2 id="att-h" style={{ margin: '0 0 16px' }}>דורש תשומת לב</h2>
          <div className="stack">
            {running.length > 0 && <Link className="tile" to="/jobs"><span className="ico"><span className="spinner" /></span><span><strong>{running.length} עבודות רצות ברקע</strong><span>יצירה, יצוא או קריאת מסמכים</span></span></Link>}
            {failed.length > 0 && <Link className="tile" to="/jobs"><span className="ico" style={{ background: 'var(--danger-soft)', color: 'var(--danger)' }}><Icon name="alert" /></span><span><strong>{failed.length} עבודות נכשלו</strong><span>{failed[0].error?.slice(0, 90)}</span></span></Link>}
            {dash.data?.pendingSpecs.map((s) => <Link key={s.id} className="tile" to={`/spec-versions/${s.id}`}><span className="ico"><Icon name="check" /></span><span><strong>מפרט ממתין לאישור: {s.media_name}</strong><span>{s.placement_name} · בדקו ואשרו כדי לייצא למסירה</span></span></Link>)}
            {dash.data?.needsReview.map((v) => <Link key={v.id} className="tile" to={`/variants/${v.id}/edit`}><span className="ico" style={{ background: 'var(--warn-soft)', color: 'var(--warn)' }}><Icon name="alert" /></span><span><strong>{v.display_name}</strong><span>{v.needs_review_reason ?? 'דורש בדיקה'}</span></span></Link>)}
          </div>
        </section>
      )}

      <section aria-labelledby="more-h">
        <h2 id="more-h" style={{ margin: '0 0 16px' }}>עוד דברים שאפשר לעשות</h2>
        <div className="grid cols-3">
          <Link className="tile" to="/import"><span className="ico"><Icon name="film" /></span><span><strong>הנפשת עיצוב קיים כמו שהוא</strong><span>PDF או פריימים → באנר מונפש, בלי לבנות מחדש</span></span></Link>
          <Link className="tile" to="/demo"><span className="ico"><Icon name="play" /></span><span><strong>בדיקה באתר דמו</strong><span>רואים את כל הבאנרים של קמפיין באתר חדשות לדוגמה</span></span></Link>
          <Link className="tile" to="/clients"><span className="ico"><Icon name="users" /></span><span><strong>לקוחות ומיתוג</strong><span>לוגו, צבעים ופונט לכל לקוח</span></span></Link>
          <Link className="tile" to="/media"><span className="ico"><Icon name="globe" /></span><span><strong>אתרי מדיה והוראות סגירה</strong><span>מידות ומפרטים מ-PDF או קישור</span></span></Link>
        </div>
      </section>
    </div>
  );
}
